import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosRequestConfig,
  type AxiosError
} from 'axios';
import { jwtDecode } from 'jwt-decode';
import { toast } from '@/components/ui/sonner';

interface JwtPayload {
  exp?: number;
}

interface CustomAxiosInternalRequestConfig extends InternalAxiosRequestConfig {
  authRequired?: boolean;
  _retry?: boolean;
}

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  authRequired?: boolean;
}

interface ErrorResponseData {
  message?: string;
  error?: string;
  details?: string;
}

// The app talks only to the machine Control Panel API, under /admin.
const MACHINE_BASE_URL =
  import.meta.env.VITE_MACHINE_BASE_URL || 'https://api.dev.kim.cc/api';
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 60000;

/**
 * Whether this build talks to a dev stack. Cloning from production exists only there (the
 * backend refuses it elsewhere), so the button is not shown to operators on production.
 */
export const IS_DEV_STACK = /\.dev\.kim\.cc|localhost/.test(MACHINE_BASE_URL);

export const ACCESS_TOKEN_KEY = 'accessToken';
export const REFRESH_TOKEN_KEY = 'refreshToken';
export const CURRENT_USER_KEY = 'currentUser';
const REFRESH_ENDPOINT = '/admin/auth/refresh';

export const AUTH_CLEARED_EVENT = 'auth:cleared';

export const api: AxiosInstance = axios.create({
  baseURL: MACHINE_BASE_URL,
  timeout: API_TIMEOUT,
  headers: { 'Content-Type': 'application/json' }
});

const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<JwtPayload>(token);
    if (!exp) return true;
    return Date.now() / 1000 >= exp;
  } catch {
    return true;
  }
};

const storeTokens = (
  accessToken: string,
  refreshToken: string,
  user?: Record<string, unknown>
) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (user) localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(CURRENT_USER_KEY);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_CLEARED_EVENT));
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
};

// Serialize concurrent refreshes so only one network call is made.
let refreshPromise: Promise<string> | null = null;

interface RefreshResponse {
  success: boolean;
  data: {
    token: string;
    refreshToken: string;
    user: Record<string, unknown>;
  };
}

const refreshAccessToken = async (): Promise<string> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const stored = getRefreshToken();
    if (!stored || isTokenExpired(stored)) {
      throw new Error('Refresh token missing or expired');
    }
    const { data } = await axios.post<RefreshResponse>(
      `${MACHINE_BASE_URL}${REFRESH_ENDPOINT}`,
      { refreshToken: stored },
      { headers: { 'Content-Type': 'application/json' } }
    );
    const { token, refreshToken, user } = data.data;
    storeTokens(token, refreshToken, user);
    return token;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

api.interceptors.request.use(
  async (config: CustomAxiosInternalRequestConfig) => {
    if (config.authRequired === false) return config;

    let accessToken = getAccessToken();
    if (accessToken && isTokenExpired(accessToken)) {
      try {
        accessToken = await refreshAccessToken();
      } catch {
        clearAuthAndRedirect();
        return Promise.reject(new Error('Token expired'));
      }
    }
    if (accessToken) {
      (config.headers as Record<string, string>)['Authorization'] =
        `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ErrorResponseData>) => {
    const data = error.response?.data;
    if (data) {
      error.message = data.message || data.error || data.details || error.message;
    }

    const original = error.config as CustomAxiosInternalRequestConfig | undefined;
    if (
      original &&
      original.authRequired !== false &&
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes(REFRESH_ENDPOINT)
    ) {
      original._retry = true;
      try {
        const newToken = await refreshAccessToken();
        (original.headers as Record<string, string>)['Authorization'] =
          `Bearer ${newToken}`;
        return api(original);
      } catch {
        clearAuthAndRedirect();
      }
    }

    if (error.response?.status === 403) {
      toast.error(error.message || 'You do not have permission to do that.');
    }

    return Promise.reject(error);
  }
);

export { storeTokens };

export default {
  get: <T = unknown>(url: string, config?: CustomAxiosRequestConfig) =>
    api.get<T>(url, config),
  post: <T = unknown>(url: string, data?: unknown, config?: CustomAxiosRequestConfig) =>
    api.post<T>(url, data, config),
  patch: <T = unknown>(url: string, data?: unknown, config?: CustomAxiosRequestConfig) =>
    api.patch<T>(url, data, config),
  put: <T = unknown>(url: string, data?: unknown, config?: CustomAxiosRequestConfig) =>
    api.put<T>(url, data, config),
  delete: <T = unknown>(url: string, config?: CustomAxiosRequestConfig) =>
    api.delete<T>(url, config)
};
